<template>
    <div class="container-fluid w-100 h-100" style="overflow-x: hidden">
        <navbar :title="$t('work_assignment.title')" class="mb-1"/>
        <work-assignment-search
            class="my-1"
            :is-observer="isObserver"
            :user="user"
            @search="searchWorkAssignments"
        />
        <div class="col-sm-12 mb-2 mt-3 d-flex justify-content-end">
            <div v-if="isObserver && selectedTableItems.length > 0" class="dropdown" style="margin-top: 0.2em">
                <button
                    class="btn btn-secondary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="true"
                    style="height: 2.15em; font-size: .9em; padding-top: .25em"
                    :disabled="selectedTableItems.length < 1"
                >
                    {{$t('common.actions')}} ({{selectedTableItems.length}})
                </button>

                <ul class="dropdown-menu">
                    <li>
                        <button class="dropdown-item" @click="showBulkEditor = true">
                            {{ $t('common.edit') }}
                        </button>
                    </li>
                    <li>
                        <button class="dropdown-item" @click="showCopyDialog = true">
                            {{ $t('common.copy') }}
                        </button>
                    </li>
                </ul>
            </div>
            <button
                v-else-if="isObserver"
                class="btn btn-secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                data-bs-auto-close="true"
                style="height: 2.15em; font-size: .9em; padding-top: .25em; margin-top: .2em"
                :disabled="true"
            >
                {{$t('common.actions')}} ({{selectedTableItems.length}})
            </button>

            <button
                :disabled="results.length < 1"
                class="btn btn-outline-success btn-sm form-button"
                @click="exportExcel"
            >
                {{ $t('trip.download_excel') }}
            </button>
            <button
                class="btn btn-outline-primary btn-sm form-button"
                :disabled="results.length < 1"
                @click="showDetailsReport = true"
            >
                {{ $t('observations.details_report') }}
            </button>
            <button class="btn btn-success  btn-sm form-button" @click.stop="addWorkAssignment()">
                {{ $t('common.add_new') }}
            </button>
            <button class="btn btn-primary btn-sm form-button" @click="toggleMapView" :disabled="results.length < 1">
                {{ mapVisible ? this.$t('trip.show_table') : this.$t('trip.show_map') }}
            </button>
        </div>
        <work-assignment-result-summary
            v-if="!showDetailsReport && results.length > 0"
            :results="results"
            :search-params="searchParams"
            :is-observer="isObserver"
        />
        <div class="trip-results" v-show="!showDetailsReport && results.length > 0">
            <div class="row">
                <div :class="{'col-md-6': mapVisible, 'col-12': !mapVisible}" class="mb-2">
                    <!-- Assignment map -->
                    <Transition name="fade">
                        <work-assignment-list
                            ref="assignmentList"
                            :results="results"
                            :selected-ids="selectedIds"
                            :is-admin="isAdmin"
                            :is-observer="isObserver"
                            @row-select="onRowSelect"
                            @edit="editDetails" @show-photos="showPhotoViewer"/>
                    </Transition>
                </div>
                <div v-if="mapVisible" class="col-md-6 d-flex flex-column pb-0 mb-2">
                    <Transition name="fade">
                        <div class="flex-fill position-relative" style="border-radius: .75em; border: 1px solid #BCBCBC; overflow: hidden">
                            <div class="map-container mr-3 ml-3 mb-2" style="height: 40em; border-radius: .75em; border: 1px solid #BCBCBC; position: relative" >
                                <map-container
                                    :find-user="false"
                                    ref="mapContainer"
                                    @on-marker-tap="onMarkerTap"
                                    @on-map-changed="mapChanged"
                                />
                                <work-assignment-map
                                    v-if="map"
                                    :user="user"
                                    :map="map"
                                    :work-assignment-results="mapItems"
                                    :zoom-on-draw-marker="autoZoom"
                                />
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
        <base-modal v-if="editMode" hide-header hide-footer @close="close" size="lg">
            <work-assignment-editor
                :area-defect="areaDefect"
                :vehicle-defect="vehicleDefect"
                :observation="observation"
                :work-permission="workPermission"
                :user="user"
                :is-observer="isObserver"
                :work-assignment="workAssignment"
                :index="selectedIndex"
                :item-count="results.length"
                @close="close"
                @closeAndUpdate="closeAndUpdate"
                @updateWorkAssignments="updateWorkAssignments"
                @nextItem="editNext"
                @previousItem="editPrevious"
            />
        </base-modal>

        <work-assignment-bulk-editor
            v-if="showBulkEditor"
            :assignments="selectedTableItems"
            :is-observer="isObserver"
            @close="showBulkEditor = false"
            @close-and-update="onBulkEditorClose"
        />

        <work-assignment-bulk-copy
            v-if="showCopyDialog"
            :assignments="selectedTableItems"
            :is-observer="isObserver"
            @close="showCopyDialog = false"
            @close-and-update="onBulkEditorClose"
        />

        <work-assignment-details-report
            v-if="showDetailsReport && reportItems.length > 0"
            :assignments = "reportItems"
            :company-preferences="companyPreferences.value"
            :user="user"
            @close="showDetailsReport = false"
        />

        <Transition name="fade">
            <work-assignment-photo-viewer
                v-if="photoAssignment"
                :assignment="photoAssignment"
                @close="photoAssignment = null"
            />
        </Transition>

        <div v-if="loading"
             id="loader"
             class="spinner">
        </div>
    </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import WorkAssignmentList from './WorkAssignmentList'
import WorkAssignmentEditor from './WorkAssignmentEditor'
import WorkAssignmentSearch from './WorkAssignmentSearch'
import WorkAssignmentMap from "./WorkAssignmentMap.vue";
import MapContainer from "@/components/map/MapContainer";
import {mapHelper} from "@/components/mixins/MapMixin";
import Navbar from "@/components/utils/Navbar";
import BaseModal from "@/components/BaseModal";
import {downloadHelper} from "../mixins/DownloadMixin.js";
import {pdfMixin} from "../mixins/PdfMixin.js";
import {workAssignmentHelper} from '../mixins/WorkAssignmentMixin';
import {timeUtils} from "../mixins/TimeUtils.js";
import WorkAssignmentDetailsReport from "./WorkAssignmentDetailsReport.vue";
import WorkAssignmentPhotoViewer from "./WorkAssignmentPhotoViewer.vue";
import WorkAssignmentBulkEditor from './WorkAssignmentBulkEditor'
import WorkAssignmentResultSummary from './WorkAssignmentResultSummary.vue';
import WorkAssignmentBulkCopy from "./WorkAssignmentBulkCopy.vue";

export default {
    name: 'WorkAssignments',
    components: {
        WorkAssignmentBulkCopy,
        WorkAssignmentResultSummary,
        WorkAssignmentPhotoViewer,
        WorkAssignmentDetailsReport,
        WorkAssignmentBulkEditor,
        BaseModal,
        Navbar,
        MapContainer,
        WorkAssignmentMap,
        WorkAssignmentList,
        WorkAssignmentEditor,
        WorkAssignmentSearch
    },
    mixins: [restApi, mapHelper, downloadHelper, pdfMixin, workAssignmentHelper, timeUtils],
    props: {
        isObserver: Boolean,
        isAdmin: Boolean,
        isMapViewer: Boolean,
        user: null,
        isMobile: Boolean
    },
    data: function () {
        return {
            loading: false,
            searchParams: null,
            results: [],
            selectedTableItems: [],
            showBulkEditor: false,
            showCopyDialog: false,
            editMode: false,
            autoZoom: true,
            map: null,
            workAssignment: null,
            selectedIndex: null,
            observation: null,
            areaDefect: null,
            vehicleDefect: null,
            workPermission: null,
            mapVisible: false,
            showDetailsReport: false,
            photoAssignment: null,
            companyPreferences: {
                id: "",
                company_id: "",
                value: {
                    observationReportTitle: ""
                },
            },
        }
    },
    computed: {
        reportItems() {
            if (this.selectedTableItems && this.selectedTableItems.length > 0){
                return this.selectedTableItems
            }
            return this.results
        },
        selectedIds() {
            return this.selectedTableItems.map(item => item.id)
        },
        mapItems() {
            if (this.selectedTableItems && this.selectedTableItems.length > 0) {
                return this.selectedTableItems
            }
            return this.results
        }
    },
    watch: {
        mapVisible() {
            if (!this.mapVisible) {
                this.map = null
            }
        }
    },
    mounted() {
        this.fetchCompanyPreferences()
    },
    methods: {

        showPhotoViewer(assignment) {
            this.photoAssignment = this.results.find(item => item.id === assignment)
        },

        fetchCompanyPreferences: function () {
            this.restFetch(this.companyPreferencesUrl, this.handleCompanyPreferenceResponse)
        },

        handleCompanyPreferenceResponse: function (response) {
            if (response.data) {
                this.companyPreferences.id = response.data.id || ""
                this.companyPreferences.company_id = response.data.company_id || ""

                // Preserve the value structure, merge with API data
                if (response.data.value && typeof response.data.value === 'object') {
                    this.companyPreferences.value = {
                        ...this.companyPreferences.value,
                        ...response.data.value
                    }
                }
            }
        },


        mapChanged(map) {
            this.map = map
        },

        onRowSelect(selectedItems) {
            this.autoZoom = true
            this.selectedTableItems = selectedItems
        },

        onBulkEditorClose() {
            this.showBulkEditor = false
            this.showCopyDialog = false
            if (this.searchParams) {
                this.fetchWorkAssignments(this.searchParams)
            }
        },

        searchWorkAssignments(params) {
            this.selectedTableItems = []
            this.$refs.assignmentList.resetSelection()
            this.autoZoom = true
            this.fetchWorkAssignments(params)
        },

        fetchWorkAssignments: function (params) {
            this.searchParams = params
            this.loading = true
            this.restFetchParams(this.workAssignmentUrl, params, this.handleResponse, this.onError)
        },
        toggleMapView: function () {
            this.mapVisible = !this.mapVisible
        },
        handleResponse: function (response) {
            this.loading = false;
            this.results = response.data ? response.data : [];
            if (this.selectedTableItems.length > 0) {
                this.selectedTableItems = this.selectedTableItems
                    .map(item => this.results.find(r => r.id === item.id))
                    .filter(item => item !== undefined)
            }
            // Check if the map is visible
            if (this.mapVisible) {
                // Use $nextTick to ensure DOM updates and reactivity are completed
                this.$nextTick(() => {
                    // Execute getMap only after results are updated and the DOM is ready
                    if (this.results.length > 0) {// Ensure results are loaded
                        this.map = this.$refs.mapContainer.getMap();
                    }
                });
            }
        },
        onError: function () {
            this.loading = false
        },
        editDetails: function (id) {
            this.workAssignment = this.results.find(item => item.id === id)
            if (this.workAssignment) {
                this.selectedIndex = this.results.findIndex(ass => ass.id === this.workAssignment.id)
                this.observation = this.workAssignment.observation
                this.areaDefect = this.workAssignment.area_defect
                this.vehicleDefect = this.workAssignment.vehicle_defect
                this.workPermission = this.workAssignment.special_work_permission
                this.editMode = true
            }
        },
        editNext() {
            this.editMode = false
            if (this.selectedIndex < this.results.length - 1) {
                this.selectedIndex++
                this.$nextTick(() => this.editDetails(this.results[this.selectedIndex].id))
            }
        },
        editPrevious() {
            this.editMode = false
            if (this.selectedIndex > 0) {
                this.selectedIndex--
                this.$nextTick(() => this.editDetails(this.results[this.selectedIndex].id))
            }
        },
        close: function () {
            this.editMode = false
        },
        updateWorkAssignments() {
            if (this.searchParams) {
                if (this.mapVisible) {
                    this.autoZoom = false
                }
                this.fetchWorkAssignments(this.searchParams)
            }
        },
        closeAndUpdate: function () {
            this.editMode = false
            this.updateWorkAssignments()
        },
        addWorkAssignment: function () {
            this.workAssignment = null
            this.observation = null
            this.areaDefect = null
            this.vehicleDefect = null
            this.workPermission = null
            this.editMode = true
            this.selectedIndex = null
        },
        onMarkerTap: function (data) {
            if (data.type === this.WORK_ASSIGNMENT) {
                this.selectedIndex = this.results.findIndex(item => item.id === data.id)
                this.$refs.assignmentList.setSelection(this.selectedIndex)
                this.$refs.assignmentList.scrollToSelected()
            }
        },
        exportExcel: function () {
            this.loading = true
            let params = this.getCommonReportParams(this.searchParams)
            if (this.selectedTableItems.length > 0) {
                params.ids = this.selectedTableItems.map(item => item.id)
            }
            this.restGetDownloadFile(this.workAssignmentReportUrl, params, this.excelDownloadSuccess, this.fileDownloadFail)
        },
        excelDownloadSuccess: function (response) {
            this.loading = false
            this.downloadFile(response, this.$t('work_assignment.report_name'))
        },
        fileDownloadFail: function () {
            this.loading = false
            this.showAlert(this.$t('common.report_generation_failed'))
        },

        getReportData: function () {
            return [
                [ // Header
                    this.$t('work_assignment.id'),
                    this.$t('work_assignment.task'),
                    this.$t('work_assignment.created_time'),
                    this.$t('work_assignment.creator'),
                    this.$t('work_assignment.assigned_to'),
                    this.$t('relationships.main_contractor'),
                    this.$t('orders.status'),
                    this.$t('work_assignment.closed_time'),
                    this.$t('area_report_details.info'),
                ],
                // Data
                ...this.results.map(item => {
                    return [
                        item.id,
                        this.getTitle(item),
                        item.time ? this.getDateString(item.time) : '',
                        item.created_by ? `${item.created_by.last_name} ${item.created_by.first_name}` : '',
                        item.assigned_to ? `${item.assigned_to.last_name} ${item.assigned_to.first_name}` : '',
                        item.assigned_company ? item.assigned_company.name : '',
                        item.status ? this.getStatusString(item.status) : '',
                        item.closing_date ? this.getDateString(item.closing_date) : '',
                        item.info ? item.info : ''
                    ]
                })
            ]
        }

    }
}
</script>
